import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../utils/api';

const ContattiContext = createContext();

export const useContatti = () => {
  const context = useContext(ContattiContext);
  if (!context) {
    throw new Error('useContatti must be used within a ContattiProvider');
  }
  return context;
};

export const ContattiProvider = ({ children }) => {
  const [contattiCache, setContattiCache] = useState({
    Sarabanda: [],
    iDO: [],
    TUTTE: []
  });
  const [loading, setLoading] = useState({
    Sarabanda: false,
    iDO: false,
    TUTTE: false
  });

  /**
   * Carica contatti per una linea specifica con caching
   * @param {string} linea - "Sarabanda", "iDO", o "TUTTE"
   * @param {boolean} forceReload - Se true, bypassa la cache e ricarica
   */
  const loadContatti = useCallback(async (linea, forceReload = false) => {
    // Se già caricati e non si forza il reload, ritorna dalla cache
    if (!forceReload && contattiCache[linea] && contattiCache[linea].length > 0) {
      return contattiCache[linea];
    }

    // Se già in caricamento, non duplicare la richiesta
    if (loading[linea]) {
      return contattiCache[linea];
    }

    setLoading(prev => ({ ...prev, [linea]: true }));
    
    try {
      const res = await api.get(`/contatti/${linea}`);
      const data = res.data || [];
      
      setContattiCache(prev => ({ ...prev, [linea]: data }));
      setLoading(prev => ({ ...prev, [linea]: false }));
      
      return data;
    } catch (error) {
      console.error(`Error loading contatti for ${linea}:`, error);
      setLoading(prev => ({ ...prev, [linea]: false }));
      return [];
    }
  }, [contattiCache, loading]);

  /**
   * Cerca contatti che matchano un nome (case-insensitive)
   * @param {string} linea - "Sarabanda", "iDO", o "TUTTE"
   * @param {string} searchTerm - termine di ricerca
   * @param {number} limit - numero massimo di risultati
   */
  const searchContatti = useCallback((linea, searchTerm, limit = 5) => {
    const contatti = contattiCache[linea] || [];
    
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }
    
    const term = searchTerm.toLowerCase();
    // Cerca sia per ragione sociale (nome) che per nome negozio (nome_punto_vendita)
    // Usa includes per trovare match parziali in qualsiasi posizione
    return contatti
      .filter(c => {
        const nomePV = (c.nome_punto_vendita || '').toLowerCase();
        const nome = (c.nome || '').toLowerCase();
        return nome.includes(term) || nomePV.includes(term);
      })
      .slice(0, limit);
  }, [contattiCache]);

  /**
   * Invalida la cache per forzare un reload al prossimo accesso
   * Utile dopo creazione/modifica/eliminazione di un contatto
   */
  const invalidateCache = useCallback((linea = null) => {
    if (linea) {
      setContattiCache(prev => ({ ...prev, [linea]: [] }));
    } else {
      // Invalida tutte le cache
      setContattiCache({
        Sarabanda: [],
        iDO: [],
        TUTTE: []
      });
    }
  }, []);

  /**
   * Ottiene tutti i contatti per una linea (carica se necessario)
   */
  const getContatti = useCallback(async (linea) => {
    if (contattiCache[linea] && contattiCache[linea].length > 0) {
      return contattiCache[linea];
    }
    return await loadContatti(linea);
  }, [contattiCache, loadContatti]);

  const value = {
    contattiCache,
    loading,
    loadContatti,
    searchContatti,
    invalidateCache,
    getContatti
  };

  return (
    <ContattiContext.Provider value={value}>
      {children}
    </ContattiContext.Provider>
  );
};

export default ContattiContext;
